import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { aiAssistantRoutes } from '@/ai-assistant/routes';
import { approvalsRoutes } from '@/approvals/router/routes';
import { ccpRoutes as ccpv2Routes } from '@/ccpv2/router/routes';
import { dataRoutes } from '@/data/router/routes';
import { dataIntegrationsRoutes } from '@/data-integrations/router/routes';
import { documentAccessRoutes } from '@/document-access/router/routes';
import { handleHelpWidgetClosing } from '@/help/router/guards';
import { homeRoutes } from '@/home/router/routes';
import { insightsRoutes } from '@/insights/router/routes';
import { redirectIfSubdomainsDisabled } from '@/login/router/guards';
import { loginRoutes } from '@/login/router/routes';
import { marketDataRoutes } from '@/market-data/router/routes';
import { marketPulseRoutes } from '@/market-pulse/router/routes';
import { onboardingRoutes } from '@/onboarding/router/routes';
import { peopleRoutes } from '@/people/router/routes';
import { playgroundRoutes } from '@/playground/router/routes';
import { rangesRoutes } from '@/ranges/router/routes';
import { displayPLoading } from '@/router/displayPLoading';
import {
  handleFirstLogin,
  handleNonLoggedInUsers,
  handleNonPublicRoutes,
  handlePBELogin,
  handleTempPasswordChange,
  loggedInActions,
  removeSplashLoader,
  savePBELoginFlag,
} from '@/router/guards';
import { handleChunkLoadError } from '@/router/handleChunkLoadError';
import { settingsRoutes } from '@/settings/router/routes';

// Route components dynamic imports
const PageNotFound = () => import('@/pages/not-found.vue');

// Default layout
const DEFAULT_LAYOUT = 'layout-default';

const history = createWebHistory('/v2/');

const routes = [
  ...loginRoutes,
  ...onboardingRoutes,
  ...homeRoutes,
  ...rangesRoutes,
  ...aiAssistantRoutes,
  ...insightsRoutes,
  ...dataRoutes,
  ...peopleRoutes,
  ...approvalsRoutes,
  ...ccpv2Routes,
  ...marketPulseRoutes,
  ...marketDataRoutes,
  ...settingsRoutes,
  ...dataIntegrationsRoutes,
  ...playgroundRoutes,
  ...documentAccessRoutes,
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: PageNotFound,
  },
].map((route: RouteRecordRaw) => {
  // Set default layout for all routes to avoid flash of content on the welcome back page
  route.meta = route.meta?.layout ? route.meta : { ...route.meta, layout: DEFAULT_LAYOUT };

  return route;
});

const router = createRouter({
  linkActiveClass: 'active',
  history,
  routes,
});

// Global guards (called in order) - https://router.vuejs.org/guide/advanced/navigation-guards.html#Global-Before-Guards
[
  redirectIfSubdomainsDisabled,
  savePBELoginFlag,
  handleNonPublicRoutes,
  handleHelpWidgetClosing,
  handleNonLoggedInUsers,
  handleFirstLogin,
  handleTempPasswordChange,
  handlePBELogin,
  loggedInActions,
  removeSplashLoader,
].forEach((guard) => {
  router.beforeEach(guard);
});

handleChunkLoadError(router);
displayPLoading(router);

export { router };
